import React from 'react';

import { useGraphQLQuery } from '@betterment/js-runtime';
import { CardLoader, ErrorBoundary } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import {
  PurposeRecurringTransactionConfigsDocument,
  PurposeRecurringTransactionConfigsQuery,
} from './graphql/purposeRecurringTransactionConfigs.generated';

import { RecurringTransactionsCardView } from '../../../Shared/components-v2/recurring-transactions-card-view/RecurringTransactionsCardView';

type LegalSubAccounts = PurposeRecurringTransactionConfigsQuery['purpose']['envelope']['legalSubAccounts'];

function depositDisclosures(legalSubAccounts: LegalSubAccounts) {
  const firstRecurringDepositMotivationAccount = legalSubAccounts
    .filter(account => account.__typename === 'ManagedSubAccount' || account.__typename === 'CashAccount')
    .find(
      account =>
        (account.__typename === 'ManagedSubAccount' && 'recurringDepositMotivation' in account.managedAccount) ||
        (account.__typename === 'CashAccount' && 'recurringDepositMotivation' in account)
    );

  if (firstRecurringDepositMotivationAccount?.__typename === 'CashAccount') {
    return firstRecurringDepositMotivationAccount.recurringDepositMotivation;
  }

  if (firstRecurringDepositMotivationAccount?.__typename === 'ManagedSubAccount') {
    return firstRecurringDepositMotivationAccount.managedAccount.recurringDepositMotivation;
  }
  return null;
}

export const RecurringTransactionsCardController = () => {
  const purposeId = useRequiredParam('purposeId');
  const { data } = useGraphQLQuery(PurposeRecurringTransactionConfigsDocument, {
    variables: { id: purposeId },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;

  const { legalSubAccounts, payrollContributionConfigs, recurringTransactionConfigs, recurringTransactionMessages } =
    data.purpose.envelope;

  const isWithdrawal = (sourceLegacySubAccountId: number) => {
    return !!legalSubAccounts.find(
      account =>
        (account.__typename === 'CashAccount' || account.__typename === 'ManagedSubAccount') &&
        account.legacySubAccountId === sourceLegacySubAccountId
    );
  };

  const hasNoConfigs = [...recurringTransactionConfigs, ...payrollContributionConfigs].length === 0;
  if (hasNoConfigs && legalSubAccounts.every(a => !a.isRecurringDepositEligible)) return null;

  return (
    <RecurringTransactionsCardView
      recurringTransactionConfigs={recurringTransactionConfigs}
      isWithdrawal={isWithdrawal}
      recurringTransactionGuidance={recurringTransactionMessages}
      payrollContributionConfigs={payrollContributionConfigs}
      disclosures={depositDisclosures(legalSubAccounts)}
      subAccountId={legalSubAccounts[0].legacySubAccountId.toString()}
    />
  );
};

export const RecurringTransactionsCard: React.FC = () => {
  return (
    <ErrorBoundary fallbackType="none">
      <RecurringTransactionsCardController />
    </ErrorBoundary>
  );
};
